// resources/js/src/services/auth.api.ts
import { RegistrationValues } from "../components/register/types/registration";
import { request } from "./api";

// ============ Types ============

export type RegisterResponse = {
  id: string;
  email: string;
  verificationCode?: string;
};

export type UserRole =
    | 'user'
    | 'company'
    | 'admin'
    | 'superadmin';

export interface User {
  id: string | number;
  name: string;
  email: string;
  role: UserRole;
  accountType?: string;   // ✅ اختياري
  status?: boolean;       // ✅ ضيف كمان
  avatarUrl?: string | null;
}

export type LoginResponse = {
  user: User
};

export type VerifyResponse = {
  user: {
    id: number | string;
    name: string;
    email: string;
    role: string;
    status: boolean;
  };
};

export type GoogleAuthResponse = {
  user: {
    id: number | string;
    email: string;
    fullName: string;
  };
  isNewUser: boolean;
};

export type AuthUserResponse = {
  id: number | string;
  name?: string;
  fullName?: string;
  email: string;
  role?: string;
  status?: boolean;
  avatarUrl?: string | null;
  accountType?: string;
};

// ============ API ============

export const authApi = {
  register: (payload: FormData) =>
    request<RegisterResponse>("POST", "/auth/register", payload),

  login: (email: string, password: string) =>
    request<LoginResponse>("POST", "/auth/login", { email, password }),

  verifyEmail: (email: string, code: string) =>
    request<VerifyResponse>("POST", "/auth/verify-email", { email, code }),

  resendVerification: (email: string) =>
    request<{ sent: boolean }>("POST", "/auth/resend-verification", { email }),

  forgotPassword: (email: string) =>
    request<{ sent: boolean }>("POST", "/auth/forgot-password", { email }),

  googleAuth: (accessToken: string) =>
    request<GoogleAuthResponse>("POST", "/auth/google", { accessToken }),

  me: () => request<AuthUserResponse>("GET", "/auth/me"),
};


/* ============ Helpers ============ */

/**
 * Normalize different user shapes into a canonical `User`.
 *
 * Handles:
 * - `name` vs `fullName`
 * - missing `role` → defaults to "user"
 * - missing `accountType` → defaults to "individual"
 * - missing `status` → defaults to true
 */
export function normalizeUser(raw: any): User {
  return {
    id: raw.id,
    name:
      raw.name ??
      raw.fullName ??
      raw.email?.split("@")[0] ??
      "User",
    email: raw.email,
    role: (raw.role ?? "user") as UserRole,
    accountType: raw.accountType ?? "individual",
    status: raw.status ?? true,
    avatarUrl: raw.avatarUrl ?? null,
  };
}

/**
 * Convert RegistrationValues to FormData for API upload.
 * Handles file uploads (company documents) and nested fields.
 */
export function registrationToFormData(data: RegistrationValues): FormData {
  const formData = new FormData();

  // Iterate over all fields
  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    // Handle File objects
    if (value instanceof File) {
      formData.append(key, value);
      return;
    }

    // Handle File[] arrays
    if (Array.isArray(value) && value.every((v) => v instanceof File)) {
      value.forEach((file) => formData.append(`${key}[]`, file));
      return;
    }

    // Handle arrays of primitives
    if (Array.isArray(value)) {
      value.forEach((v) => formData.append(`${key}[]`, String(v)));
      return;
    }

    // Handle booleans → "1"/"0"
    if (typeof value === "boolean") {
      formData.append(key, value ? "1" : "0");
      return;
    }

    // Handle primitives
    formData.append(key, String(value));
  });

  return formData;
}
